import React from 'react';
import { ArrowDownToLine, Plus, Upload } from 'lucide-react';
import { Card } from '../../../ui/card';
import { Button } from '../../../ui/button';
import { Input } from '../../../ui/input';
import { MEMBER_TIER_OPTIONS } from './memberConstants';

const PRIMARY_QUEUE_FILTERS = ['prospect', 'warm_but_later', 'unresponsive', 'not_interested'];

const MembersToolbar = ({
  searchTerm,
  setSearchTerm,
  tierFilter,
  setTierFilter,
  onOpenDadImport,
  onOpenWebinarImport,
  onOpenAddMember,
  joinFirstMode,
  displayedStageOptions,
  stageFilter,
  setStageFilter,
  stageCounts,
  showRemoved,
  setShowRemoved,
  getStageFilterButtonClass,
}) => {
  const primaryQueueOptions = displayedStageOptions.filter((option) =>
    PRIMARY_QUEUE_FILTERS.includes(option.value),
  );

  return (
    <>
      <Card className="border-[#2a2a32] bg-[#1e1e24] p-4">
        <div className="flex flex-wrap items-center gap-4">
          <div className="min-w-[200px] flex-1">
            <Input
              placeholder="Search dads..."
              value={searchTerm}
              onChange={(event) => setSearchTerm(event.target.value)}
              className="border-[#2a2a32] bg-[#16161a] text-white"
            />
          </div>
          <select
            value={tierFilter}
            onChange={(event) => setTierFilter(event.target.value)}
            className="rounded-lg border border-[#2a2a32] bg-[#16161a] px-3 py-2 text-white"
          >
            <option value="all">All Tiers</option>
            <option value="prospect">Dads to Invite</option>
            {MEMBER_TIER_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
          <Button
            variant="outline"
            onClick={onOpenDadImport}
            className="gap-2 border-gray-300 bg-white text-gray-700 hover:bg-gray-100 dark:border-[#2a2a32] dark:bg-[#16161a] dark:text-white dark:hover:bg-[#202028]"
          >
            <Upload className="h-4 w-4" />
            Import Dads
          </Button>
          <Button
            variant="outline"
            onClick={onOpenWebinarImport}
            className="gap-2 border-gray-300 bg-white text-gray-700 hover:bg-gray-100 dark:border-[#2a2a32] dark:bg-[#16161a] dark:text-white dark:hover:bg-[#202028]"
          >
            <ArrowDownToLine className="h-4 w-4" />
            {joinFirstMode ? 'Import Attendance Results' : 'Import Webinar Results'}
          </Button>
          <Button
            onClick={onOpenAddMember}
            className="gap-2 bg-aqua text-charcoal hover:bg-aqua-dark"
          >
            <Plus className="h-4 w-4" />
            Add Dad
          </Button>
        </div>
      </Card>

      <Card className="border border-gray-200 bg-white p-3 dark:border-[#2a2a32] dark:bg-[#1e1e24]">
        {joinFirstMode ? (
          <div className="mb-3 flex flex-wrap items-center gap-2">
            <span className="rounded-full border border-orange-200 bg-orange-50 px-2.5 py-1 text-[11px] font-semibold uppercase tracking-[0.18em] text-orange-700 dark:border-orange-500/20 dark:bg-orange-500/10 dark:text-orange-300">
              Focus now
            </span>
            <span className="text-xs text-gray-600 dark:text-gray-400">
              Founding 40 joined comes first in join mode.
            </span>
          </div>
        ) : null}
        {primaryQueueOptions.length ? (
          <div className="mb-3 rounded-xl border border-gray-200 bg-gray-50/80 p-3 dark:border-[#2a2a32] dark:bg-[#16161a]">
            <div className="mb-2 flex flex-wrap items-center gap-2">
              <span className="rounded-full border border-cyan-200 bg-cyan-50 px-2.5 py-1 text-[11px] font-semibold uppercase tracking-[0.18em] text-cyan-800 dark:border-cyan-500/20 dark:bg-cyan-500/10 dark:text-cyan-300">
                Review queues
              </span>
              <span className="text-xs text-gray-600 dark:text-gray-400">
                Keep active invites separate from colder follow-up lists.
              </span>
            </div>
            <div className="flex flex-wrap gap-2">
              {primaryQueueOptions.map((option) => {
                const isActive = stageFilter === option.value;
                return (
                  <button
                    key={option.value}
                    type="button"
                    onClick={() => setStageFilter(option.value)}
                    className={`rounded-full border px-3 py-1.5 text-sm transition-colors ${getStageFilterButtonClass({
                      value: option.value,
                      isActive,
                      joinFirstMode,
                    })}`}
                  >
                    {option.label}
                    <span className={`ml-2 text-xs ${isActive ? 'text-charcoal/80' : 'text-gray-500 dark:text-gray-500'}`}>
                      {stageCounts[option.value] || 0}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>
        ) : null}
        <div className="flex flex-wrap items-center gap-2">
          {displayedStageOptions.map((option) => {
            const isActive = stageFilter === option.value;
            return (
              <button
                key={option.value}
                type="button"
                onClick={() => setStageFilter(option.value)}
                className={`rounded-full border px-3 py-1.5 text-sm transition-colors ${getStageFilterButtonClass({
                  value: option.value,
                  isActive,
                  joinFirstMode,
                })}`}
              >
                {option.label}
                <span className={`ml-2 text-xs ${isActive ? 'text-charcoal/80' : 'text-gray-500 dark:text-gray-500'}`}>
                  {stageCounts[option.value] || 0}
                </span>
              </button>
            );
          })}
        </div>
        <div className="mt-3 flex flex-wrap items-center gap-4">
          <label className="flex cursor-pointer items-center gap-2 rounded px-2 py-1 text-sm text-gray-900 transition-colors hover:bg-gray-100 dark:text-white dark:hover:bg-[#16161a]">
            <input
              type="checkbox"
              checked={showRemoved}
              onChange={(event) => setShowRemoved(event.target.checked)}
              className="h-4 w-4 rounded border border-gray-300 bg-white accent-aqua dark:border-[#2a2a32] dark:bg-[#16161a]"
            />
            <span>Show Removed/Former</span>
          </label>
        </div>
      </Card>
    </>
  );
};

export default MembersToolbar;
